/**
 * A setting field's shape and its input helpers (TMMPDM-0088; moved out of
 * `EffectiveDatedSettings.tsx` by TMMPDM-0090 so the rate card create dialog uses the same checks):
 * the text an input starts with, the bound check before sending, and the wire value.
 */
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import { majorFromMinor, majorToMinor, minorUnitDigits } from '@/format';

export interface SettingField {
  /** The wire field. */
  key: string;
  label: string;
  help: string;
  kind: 'money' | 'integer' | 'percentBp' | 'text' | 'select';
  /** integer: inclusive; money: in major units; percentBp: 0–100 as shown. */
  min?: number;
  max?: number;
  /** text */
  maxLength?: number;
  /** text: empty is allowed, and leaves the key out of the body (TMMPDM-0103) */
  nullable?: boolean;
  /** select: the options, as sent. Required. */
  options?: readonly string[];
  /** select: the words for an option (TMMPDM-0104); an option without one is shown as sent. */
  optionLabels?: Readonly<Record<string, string>>;
  /** The value the schedule dialog starts with when there is no current row (TMMPDM-0104). */
  initial?: string;
  /** The words for a server 400 naming this field (TMMPDM-0104). */
  rejected?: string;
  /**
   * select: the options from the dialog's other values (TMMPDM-0105); in the dialog they replace
   * `options`. When the chosen value isn't among them, it moves to the current row's value, else
   * `initial`, else the first option, whichever is among them.
   */
  optionsFor?: (values: Readonly<Record<string, string>>) => readonly string[];
  /** A line under the field from the dialog's values (TMMPDM-0105), beside `help`. */
  hintFor?: (values: Readonly<Record<string, string>>) => ReactNode;
  /**
   * The words for a server 400 naming this field, from the dialog's values (TMMPDM-0105). A
   * `{ key, text }` puts the words on another field instead (TMMPDM-0106).
   */
  rejectedFor?: (values: Readonly<Record<string, string>>) => string | FieldRejection;
  /** select: shown beside a value, in the options and the Current and Scheduled lists (TMMPDM-0105). */
  adornment?: (value: string) => ReactNode;
  /**
   * select: why an option can't be chosen, or null (TMMPDM-0106). Such an option stays listed but
   * disabled, with the reason under it, and the dialog never starts on it.
   */
  unavailable?: (value: string) => string | null;
  /**
   * A read-only line shown in place of the field, from the dialog's values, or null (TMMPDM-0106).
   * While a field is replaced, Schedule is off.
   */
  replacedFor?: (values: Readonly<Record<string, string>>) => ReactNode;
  /**
   * select: a warning under a value in the Current and Scheduled lists, or null (TMMPDM-0106). It
   * carries a Schedule a change… link.
   */
  warningFor?: (value: string) => string | null;
  /**
   * select: a note under an option that stays enabled, or null (TMMPDM-0108); `unavailable` wins
   * when both apply.
   */
  optionNote?: (value: string) => string | null;
  /**
   * An action inside the field's error area, from the dialog's values, or null (TMMPDM-0108): a
   * button that fixes the cause in place. `run` resolves to null when it worked (the error clears)
   * or to the words for why it didn't (shown in the error area, the button stays).
   */
  errorActionFor?: (values: Readonly<Record<string, string>>) => FieldErrorAction | null;
}

/** A button in a field's error area (TMMPDM-0108). */
export interface FieldErrorAction {
  label: string;
  ariaLabel: string;
  busyLabel: string;
  run: () => Promise<string | null>;
}

/** A server 400's words on a field other than the one it named (TMMPDM-0106). */
export interface FieldRejection {
  key: string;
  text: string;
}

/** A select option's words: its label, else the value as sent. */
export function optionLabel(field: SettingField, value: string): string {
  return field.optionLabels?.[value] ?? value;
}

/** Field value → the text its input starts with. */
export function inputValue(
  field: SettingField,
  value: unknown,
  currency: string | undefined,
): string {
  if (value === null || value === undefined) return '';
  if (field.kind === 'select') return typeof value === 'string' ? value : '';
  if (field.kind === 'money' && typeof value === 'number')
    return majorFromMinor(value, currency ?? '');
  if (field.kind === 'percentBp' && typeof value === 'number') return String(value / 100);
  return String(value);
}

export function useFieldCheck() {
  const { t } = useCopy();
  return (field: SettingField, raw: string, currency: string | undefined): string | null => {
    const value = raw.trim();
    if (field.kind === 'select') {
      if (value === '' || (field.options !== undefined && !field.options.includes(value))) {
        return t('datedSettings.form.required', 'Required.');
      }
      return null;
    }
    if (field.kind === 'text') {
      if (!field.nullable && value === '') return t('datedSettings.form.required', 'Required.');
      if (field.maxLength !== undefined && value.length > field.maxLength) {
        return t('datedSettings.form.tooLong', 'Use at most {{max, number}} characters.', {
          max: field.maxLength,
        });
      }
      return null;
    }
    const digits =
      field.kind === 'money' ? minorUnitDigits(currency ?? '') : field.kind === 'percentBp' ? 2 : 0;
    const pattern = digits > 0 ? new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`) : /^\d+$/;
    const min = field.kind === 'percentBp' ? (field.min ?? 0) : field.min;
    const max = field.kind === 'percentBp' ? (field.max ?? 100) : field.max;
    const range = () =>
      min !== undefined && max !== undefined
        ? t('datedSettings.form.between', 'Must be between {{min, number}} and {{max, number}}.', {
            min,
            max,
          })
        : min !== undefined
          ? t('datedSettings.form.atLeast', 'Must be at least {{min, number}}.', { min })
          : max !== undefined
            ? t('datedSettings.form.atMost', 'Must be at most {{max, number}}.', { max })
            : t('datedSettings.form.number', 'Enter a number.');
    if (!pattern.test(value)) {
      return digits === 0 && /^\d+\.\d+$/.test(value)
        ? t('datedSettings.form.whole', 'Must be a whole number.')
        : range();
    }
    const n = Number(value);
    if ((min !== undefined && n < min) || (max !== undefined && n > max)) return range();
    return null;
  };
}

/**
 * The wire value that leaves the key out of the body. Admin request bodies never clear on `null`
 * (tmm-api 0167): omitted means unchanged or the default.
 */
export const OMIT: unique symbol = Symbol('omit');

export function toWire(field: SettingField, raw: string, currency: string | undefined): unknown {
  const value = raw.trim();
  switch (field.kind) {
    case 'money':
      return majorToMinor(value, currency ?? '');
    case 'integer':
      return Number(value);
    case 'percentBp':
      return Math.round(Number(value) * 100);
    case 'select':
      return value;
    default:
      return value === '' && field.nullable ? OMIT : value;
  }
}
